import Badge from "@/components/Badge";
import Text from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getRestaurantsInGrid,
  getRestaurantsInGridState,
  resetRestaurantsInGridState,
} from "@/store/getRestaurantsInGridState";
import {
  ArrowUpDown,
  Calendar,
  Columns3,
  Download,
  Eye,
  Funnel,
  RefreshCcw,
} from "lucide-react";
import { useCallback, useEffect, useState, type FC } from "react";
import { useNavigate, useParams } from "react-router";
import DrawerColumns from "../components/DrawerColumns";
import { getGrids, getGridsState } from "@/store/getGridsState";
import { updateGrid, updateGridState } from "@/store/updateGridState";
import DrawerFilter from "../components/DrawerFilter";
import { useTranslation } from "react-i18next";
import { formatPromotionToText } from "@/lib/utils";
import { DAYS } from "@/lib/constants";
import dayjs from "dayjs";
import type { RestaurantDetails, RestaurantOpeningHours } from "@/API";
import { Tooltip, TooltipContent } from "@/components/ui/tooltip";
import { TooltipTrigger } from "@radix-ui/react-tooltip";
import { exportGrid } from "@/store/exportGridState";
import { toast } from "sonner";
import PopoverPlanificationExport from "../components/PopoverPlanificationExport";
import { Skeleton } from "@/components/ui/skeleton";
import DrawerPromotionDetails from "../components/DrawerPromotionDetails";
import DrawerMenuDetails from "../components/DrawerMenuDetails";
import {
  refreshRestaurants,
  refreshRestaurantsState,
  resetRefreshRestaurantsState,
} from "@/store/refreshRestaurantsState";

export const FILTERS_GRID_BUILDER = [
  "promo_inactive",
  "low_score",
  "long_delivery_time",
  "low_rating",
];

const orderDay = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const numberOfCell = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

type ShiftTooltip = {
  [day: string]: {
    startTime: string;
    endTime: string;
  }[];
};

type SortType =
  | "krunsh_score-asc"
  | "krunsh_score-desc"
  | "rating-asc"
  | "rating-desc"
  | "delivery_time-asc"
  | "delivery_time-desc"
  | "reviews_count-asc"
  | "reviews_count-desc";

const Grid: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { gridId } = useParams<{ gridId: string }>();
  const {
    data: restaurantsList,
    isLoading: restaurantsIsloading,
    isFetching: restaurantsisFetching,
  } = getRestaurantsInGridState();
  const { data: grids } = getGridsState();
  const { data: updatedGrid, isLoading: isUpdatingGrid } = updateGridState();
  const { data: restaurantsRefreshed } = refreshRestaurantsState();

  restaurantsList?.data.forEach((restaurant) => {
    console.log(
      "date",
      dayjs(restaurant?.lastRefreshedAt).format("mm:HH DD/MM/YYYY"),
    );
  });

  const [columns, setColumns] = useState<string[]>([]);
  const [initColumns, setInitColumns] = useState<string[]>([]);
  const [filters, setFilters] = useState<string[]>(["ubereats", "deliveroo"]);
  const [sort, setSort] = useState<null | SortType>(null);
  const [openRestaurantPromotions, setOpenRestaurantPromotions] =
    useState<null | RestaurantDetails>(null);
  const [openRestaurantMenu, setOpenRestaurantMenu] =
    useState<null | RestaurantDetails>(null);
  const [refreshingRestaurants, setRefreshingRestaurants] = useState(false);

  useEffect(() => {
    if (gridId) getRestaurantsInGrid({ gridId });

    return () => resetRestaurantsInGridState();
  }, [gridId]);

  useEffect(() => {
    if (!grids || grids.length === 0) {
      getGrids();
    }
  }, [grids]);

  useEffect(() => {
    if (restaurantsRefreshed && gridId) {
      setTimeout(() => {
        setRefreshingRestaurants(false);

        toast.success(t("toast_refreshed_restaurants"), { duration: 1000 });
        getRestaurantsInGrid({ gridId });
      }, 10000);
    }

    return () => {
      resetRefreshRestaurantsState();
    };
  }, [restaurantsRefreshed, gridId]);

  useEffect(() => {
    if (gridId && grids) {
      const grid = grids?.find((grid) => grid.id === gridId);
      if (grid && grid.columns) {
        setColumns(grid.columns.filter((it) => it !== null));
        setInitColumns(grid.columns.filter((it) => it !== null));
      }
    }
  }, [gridId, grids]);

  const updateGridCallback = useCallback(
    (cols: string[]) => {
      if (gridId && grids && restaurantsList?.data) {
        const grid = grids?.find((grid) => grid.id === gridId);
        if (grid) {
          updateGrid({
            grid: {
              ...grid,
              columns: cols,
            },
            restaurantsId: restaurantsList.data
              .filter((restaurant) => restaurant !== null)
              .map((restaurant) => restaurant.id),
          });
        }
      }
    },
    [grids, gridId, restaurantsList],
  );

  const updateSortCallback = useCallback(
    (sortType: SortType) => {
      if (!sort) {
        setSort(sortType);
        return;
      }

      const sameSortType = sort.split("-")[0] === sortType.split("-")[0];

      if (sameSortType) {
        setSort(
          sort.includes("asc")
            ? (sort.replace("asc", "desc") as SortType)
            : (sort.replace("desc", "asc") as SortType),
        );
      }

      if (!sameSortType) {
        setSort(sortType);
      }
    },
    [sort],
  );

  let restaurantsFiltered =
    restaurantsList?.data.filter((restaurant) => restaurant !== null) ?? [];
  let averageDeliveryTime = 0;
  let restaurantsWith20PourcentBetterRank: string[] = [];
  let restaurantsWith20PourcentWorstRank: string[] = [];

  restaurantsFiltered = restaurantsFiltered.filter((restaurant) => {
    if (filters.includes("ubereats")) {
      return restaurant.platform === "UBEREATS";
    }

    if (filters.includes("deliveroo")) {
      return restaurant.platform === "DELIVEROO";
    }
  });

  if (filters.includes("option_without_promotion")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant.promotionCount === 0,
    );
  }

  if (filters.includes("option_with_promotion")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant.promotionCount > 0,
    );
  }

  if (filters.includes("krunsh_score_more_less")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant.krunshScore <= 60,
    );
  }

  if (filters.includes("krunsh_score_more_than")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant.krunshScore >= 60,
    );
  }

  if (filters.includes("average_rating_less_tan")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => Number(restaurant.rating) < 4,
    );
  }

  if (filters.includes("average_rating_more_tan")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => Number(restaurant.rating) > 4,
    );
  }

  if (filters.includes("average_time_less_than")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant.deliveryTime < 10,
    );
  }

  if (filters.includes("average_time_more_than")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant.deliveryTime > 10,
    );
  }

  if (filters.includes("rating_count_less_than")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant?.ratingCount ?? 0 < 10,
    );
  }

  if (filters.includes("rating_count_more_than")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant?.ratingCount ?? 0 > 10,
    );
  }

  if (filters.includes("with_hero_image")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant?.imageUrl,
    );
  }

  if (filters.includes("without_hero_image")) {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => !restaurant.imageUrl,
    );
  }

  if (filters.includes("open")) {
    restaurantsFiltered = restaurantsFiltered.filter((restaurant) => {
      const findOpeningHours = restaurant.openingHours
        .filter((it) => it !== null)
        .filter((it) => it.day === DAYS[dayjs().day()])
        .filter(
          (it) =>
            dayjs()
              .set("hours", parseInt(it.startTime.split(":")[0]))
              .set("minutes", parseInt(it.startTime.split(":")[1]))
              .isAfter(dayjs()) &&
            dayjs().isBefore(
              dayjs()
                .set("hours", parseInt(it.endTime.split(":")[0]))
                .set("minutes", parseInt(it.endTime.split(":")[1])),
            ),
        );

      return findOpeningHours.length > 0;
    });
  }

  if (filters.includes("closed")) {
    restaurantsFiltered = restaurantsFiltered.filter((restaurant) => {
      const findOpeningHours = restaurant.openingHours
        .filter((it) => it !== null)
        .filter((it) => it.day === DAYS[dayjs().day()])
        .filter(
          (it) =>
            dayjs()
              .set("hours", parseInt(it.startTime.split(":")[0]))
              .set("minutes", parseInt(it.startTime.split(":")[1]))
              .isAfter(dayjs()) &&
            dayjs().isBefore(
              dayjs()
                .set("hours", parseInt(it.endTime.split(":")[0]))
                .set("minutes", parseInt(it.endTime.split(":")[1])),
            ),
        );

      return findOpeningHours.length === 0;
    });
  }

  averageDeliveryTime = restaurantsFiltered.reduce(
    (acc, restaurant) => acc + restaurant.deliveryTime,
    0,
  );

  averageDeliveryTime =
    restaurantsFiltered.length > 0
      ? Math.round(averageDeliveryTime / restaurantsFiltered.length)
      : 0;

  restaurantsWith20PourcentBetterRank = restaurantsFiltered
    .filter((restaurant) => {
      const nearbyRestaurants = restaurant.nearbyRestaurants.restaurants.filter(
        (restaurantNearby) => restaurantNearby !== null,
      );

      const rank =
        nearbyRestaurants.find(
          (restaurantNearby) => restaurantNearby.id === restaurant.id,
        )?.index ?? 0;

      return rank < nearbyRestaurants.length * 0.8;
    })
    .map((restaurant) => restaurant.id);

  restaurantsWith20PourcentWorstRank = restaurantsFiltered
    .filter((restaurant) => {
      const nearbyRestaurants = restaurant.nearbyRestaurants.restaurants.filter(
        (restaurantNearby) => restaurantNearby !== null,
      );

      const rank =
        nearbyRestaurants.find(
          (restaurantNearby) => restaurantNearby.id === restaurant.id,
        )?.index ?? 0;

      return rank > nearbyRestaurants.length * 0.8;
    })
    .map((restaurant) => restaurant.id);

  if (sort) {
    if (sort === "krunsh_score-asc") {
      restaurantsFiltered.sort((a, b) => a.krunshScore - b.krunshScore);
    }
    if (sort === "krunsh_score-desc") {
      restaurantsFiltered.sort((a, b) => b.krunshScore - a.krunshScore);
    }

    if (sort === "rating-asc") {
      restaurantsFiltered.sort((a, b) => Number(a?.rating) - Number(b?.rating));
    }
    if (sort === "rating-desc") {
      restaurantsFiltered.sort((a, b) => Number(b?.rating) - Number(a?.rating));
    }

    if (sort === "delivery_time-asc") {
      restaurantsFiltered.sort((a, b) => a.deliveryTime - b.deliveryTime);
    }
    if (sort === "delivery_time-desc") {
      restaurantsFiltered.sort((a, b) => b.deliveryTime - a.deliveryTime);
    }

    if (sort === "reviews_count-asc") {
      restaurantsFiltered.sort(
        (a, b) =>
          Number(a?.ratingCount?.replace("+", "") ?? 0) -
          Number(b?.ratingCount?.replace("+", "") ?? 0),
      );
    }
    if (sort === "reviews_count-desc") {
      restaurantsFiltered.sort(
        (a, b) =>
          Number(b?.ratingCount?.replace("+", "") ?? 0) -
          Number(a?.ratingCount?.replace("+", "") ?? 0),
      );
    }
  }

  if (!sort) {
    restaurantsFiltered.sort((a, b) => {
      const addressA = a.address ?? "";
      const addressB = b.address ?? "";

      return addressA.localeCompare(addressB);
    });
  }

  const averageKrunshScore =
    restaurantsFiltered.length > 0
      ? restaurantsFiltered.reduce(
          (acc, restaurant) => acc + restaurant.krunshScore,
          0,
        ) / restaurantsFiltered.length
      : 0;

  const numberOfActivePromotions = restaurantsFiltered.reduce(
    (acc, restaurant) => acc + restaurant.promotionCount,
    0,
  );

  const averageNumberOfReviews =
    restaurantsFiltered.length > 0
      ? restaurantsFiltered.reduce(
          (acc, restaurant) =>
            acc + Number(restaurant.ratingCount?.replace("+", "") ?? 0),
          0,
        ) / restaurantsFiltered.length
      : 0;

  return (
    <>
      <DrawerPromotionDetails
        open={Boolean(openRestaurantPromotions)}
        restaurant={openRestaurantPromotions}
        onClose={() => {
          setOpenRestaurantPromotions(null);
        }}
      />

      <DrawerMenuDetails
        open={Boolean(openRestaurantMenu)}
        restaurant={openRestaurantMenu}
        onClose={() => {
          setOpenRestaurantMenu(null);
        }}
      />

      <div
        style={{
          height: "calc(100dvh - var(--height-header))",
        }}
        className="bg-[#F7F8F9] py-[40px] pl-[40px] overflow-y-auto overflow-x-hidden flex flex-col"
      >
        <div className="flex justify-between pr-[40px]">
          <Text fontSize={20} fontWeight={600}>
            {grids?.find((grid) => grid.id === gridId)?.name}
          </Text>

          <div className="flex gap-[12px]">
            <DrawerColumns
              columns={columns}
              setColumns={setColumns}
              initColumns={initColumns}
              updateGridCallback={updateGridCallback}
            >
              <Button variant="whiteFilled">
                <Columns3 />
                <Text fontSize={14} fontWeight={500} color="#393939">
                  {t("columns")}
                </Text>
              </Button>
            </DrawerColumns>
            <DrawerFilter filters={filters} setFilters={setFilters}>
              <Button variant="whiteFilled">
                <Funnel />
                <Text fontSize={14} fontWeight={500} color="#393939">
                  {t("filters")}
                </Text>
              </Button>
            </DrawerFilter>

            <Button
              variant="whiteFilled"
              onClick={() => {
                if (gridId) {
                  toast("Votre export est en cours de préparation...", {
                    duration: 2000,
                  });
                  exportGrid({ gridId });
                }
              }}
            >
              <Download />
              <Text fontSize={14} fontWeight={500} color="#393939">
                {t("export_csv")}
              </Text>
            </Button>
            <PopoverPlanificationExport
              grid={grids?.find((grid) => grid.id === gridId)}
              restaurants={
                restaurantsList?.data?.filter((r) => r !== null) ?? []
              }
              updatedGrid={Boolean(updatedGrid)}
              isUpdatingGrid={isUpdatingGrid}
            >
              <Button variant="whiteFilled">
                <Calendar />
                <Text fontSize={14} fontWeight={500} color="#393939">
                  {t("planificate")}
                </Text>
              </Button>
            </PopoverPlanificationExport>
          </div>
        </div>

        <div className="flex gap-[8px] mt-[8px] mb-[24px] pr-[40px]">
          {restaurantsIsloading ? (
            <Skeleton className="h-[26px] w-[80px] rounded-[40px]" />
          ) : (
            <Badge>
              <Text fontSize={14} fontWeight={600}>
                {t("count_addresses", { count: restaurantsFiltered.length })}
              </Text>
            </Badge>
          )}

          <Badge>
            <Text fontSize={14} fontWeight={600}>
              {t("count_columns", { count: columns.length })}
            </Text>
          </Badge>
        </div>

        <div className="mb-[16px]">
          <Button
            variant="whiteFilled"
            disabled={restaurantsisFetching || refreshingRestaurants}
            onClick={() => {
              refreshRestaurants({
                restaurantIds:
                  restaurantsList?.data
                    .map((restaurant) => restaurant?.id)
                    .filter((id) => id !== undefined) ?? [],
              });

              toast.info(t("toast_refreshing_restaurants"), {
                duration: 10000,
              });

              setRefreshingRestaurants(true);
            }}
          >
            <RefreshCcw />
            {t("refresh")}
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid sm:grid-cols-4 gap-[16px] sm:overflow-x-auto sm:min-h-[88px] mb-[32px] pr-[40px]">
          {/* KPI 1 */}
          <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-h-[88px]">
            <Text fontSize={12} fontWeight={400}>
              {t("average_krunsh_score")}
            </Text>

            <Text fontSize={20} color="#172B4D" fontWeight={600}>
              {averageKrunshScore?.toFixed(0)}
            </Text>
          </div>
          {/* KPI 2 */}
          <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-h-[88px]">
            <Text fontSize={12} fontWeight={400}>
              {t("delivery_time_average")}
            </Text>

            <Text fontSize={20} color="#172B4D" fontWeight={600}>
              {averageDeliveryTime}
            </Text>
          </div>
          {/* KPI 3 */}
          <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-h-[88px]">
            <Text fontSize={12} fontWeight={400}>
              {t("active_promotions_no_count")}
            </Text>

            <Text fontSize={20} color="#172B4D" fontWeight={600}>
              {numberOfActivePromotions}
            </Text>
          </div>
          {/* KPI 4 */}
          <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-h-[88px]">
            <Text fontSize={12} fontWeight={400}>
              {t("average_reviews_count")}
            </Text>

            <Text fontSize={20} color="#172B4D" fontWeight={600}>
              {averageNumberOfReviews?.toFixed(0)}
            </Text>
          </div>
        </div>

        <Table className="mr-[40px]">
          <TableHeader
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "#F7F8F9",
              zIndex: 2,
            }}
          >
            <TableRow>
              <TableHead
                style={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  backgroundColor: "white",
                  border: "1px solid #D5D7DA",
                }}
                className="text-center"
              >
                {t("grid_table_name")}
              </TableHead>
              {columns.includes("hero_image") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  {t("grid_table_hero_image")}
                </TableHead>
              )}
              {columns.includes("krunsh_score") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  <Button
                    variant="secondaryOutlined"
                    onClick={() => updateSortCallback("krunsh_score-asc")}
                  >
                    {t("grid_table_krunsh_score")}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
              )}
              {columns.includes("rank") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  {t("grid_table_krunsh_rank")}
                </TableHead>
              )}
              {columns.includes("rating") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  <Button
                    variant="secondaryOutlined"
                    onClick={() => updateSortCallback("rating-asc")}
                  >
                    {t("grid_table_rating")}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
              )}
              {columns.includes("reviews_count") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  <Button
                    variant="secondaryOutlined"
                    onClick={() => updateSortCallback("reviews_count-asc")}
                  >
                    {t("grid_table_reviews_count")}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
              )}
              {columns.includes("delivery_time") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  <Button
                    variant="secondaryOutlined"
                    onClick={() => updateSortCallback("delivery_time-asc")}
                  >
                    {t("grid_table_delivery_time")}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
              )}
              {columns.includes("promotion_active") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  {t("grid_table_promotion_active")}
                </TableHead>
              )}
              {columns.includes("promotion_type") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  {t("grid_table_promotion_type")}
                </TableHead>
              )}
              {columns.includes("menu_items") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  {t("grid_table_menu_items")}
                </TableHead>
              )}
              <TableHead
                style={{
                  border: "1px solid #D5D7DA",
                }}
                className="text-center"
              >
                {t("grid_table_menu_items_image_missing")}
              </TableHead>
              <TableHead
                style={{
                  border: "1px solid #D5D7DA",
                }}
                className="text-center"
              >
                {t("grid_table_menu_items_description_missing")}
              </TableHead>
              {columns.includes("shifts") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  {t("grid_table_shifts")}
                </TableHead>
              )}
              {columns.includes("categories") && (
                <TableHead
                  style={{
                    border: "1px solid #D5D7DA",
                  }}
                  className="text-center"
                >
                  {t("grid_table_categories")}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          {restaurantsIsloading ? (
            <TableBody className="bg-[white]">
              {orderDay.map((key) => (
                <TableRow key={`skeleton-${key}`}>
                  {numberOfCell.map((keyCell) => (
                    <TableCell
                      style={{ border: "1px solid #D5D7DA" }}
                      key={`skeleton-${key}-${keyCell}`}
                    >
                      <Skeleton className="h-[28px] w-[160px] rounded-[24px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody className="bg-[white]">
              {restaurantsFiltered.map((restaurant) => {
                let statusShifts = "N/A";
                const nearbyRestaurants =
                  restaurant.nearbyRestaurants.restaurants.filter(
                    (restaurantNearby) => restaurantNearby !== null,
                  );

                const rank =
                  nearbyRestaurants.find(
                    (restaurantNearby) => restaurantNearby.id === restaurant.id,
                  )?.index ?? 0;

                let openingHoursOfToday: null | RestaurantOpeningHours[] = null;

                if (
                  restaurant.openingHours &&
                  restaurant.openingHours.length > 0
                ) {
                  const findOpeningHours = restaurant.openingHours
                    .filter((it) => it !== null)
                    .filter((it) => it.day === DAYS[dayjs().day()]);

                  statusShifts = "closed";

                  if (findOpeningHours && findOpeningHours.length > 0) {
                    openingHoursOfToday = findOpeningHours;
                    statusShifts = "shift";
                  }
                }

                const promotionsText = [
                  ...new Set(
                    restaurant.promotions
                      .filter((promo) => promo !== null)
                      .map((promo) => formatPromotionToText(promo, t)),
                  ),
                ].join(", ");

                const itemsText = restaurant.menuItems
                  .filter((item) => item !== null)
                  .map((item) => item.name)
                  .join(", ");

                const categoriesText = restaurant.categories
                  ?.filter((category) => category !== null)
                  .join(", ");

                const shiftsText =
                  openingHoursOfToday &&
                  openingHoursOfToday
                    .map((openingHours) =>
                      openingHours
                        ? `${openingHours.startTime} - ${openingHours.endTime}`
                        : "N/A",
                    )
                    .join(", ");

                let shiftTooltip: ShiftTooltip = {};

                restaurant.openingHours
                  .filter((it) => it !== null)
                  .forEach(({ day, startTime, endTime }) => {
                    if (shiftTooltip[day]) {
                      shiftTooltip[day].push({
                        startTime,
                        endTime,
                      });
                    } else {
                      shiftTooltip[day] = [{ startTime, endTime }];
                    }
                  });

                return (
                  <TableRow key={restaurant.id}>
                    <TableCell
                      style={{
                        position: "sticky",
                        backgroundColor: "white",
                        left: 0,
                        border: "1px solid #D5D7DA",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        navigate(`/restaurant/${restaurant.id}`);
                      }}
                    >
                      <div className="flex gap-[8px] items-center">
                        <img
                          src={`/partner/${restaurant.platform.toLowerCase()}.png`}
                          className="rounded-[50%] h-[24px] w-[24px]"
                        />
                        <div className="flex flex-col gap-[4px] max-w-[300px] pr-[20px]">
                          <Text
                            fontWeight={500}
                            fontSize={14}
                            className="truncate"
                          >
                            {restaurant.name}
                          </Text>
                          <Text
                            fontWeight={300}
                            fontSize={14}
                            className="truncate"
                          >
                            {restaurant.address}
                          </Text>
                        </div>
                      </div>
                    </TableCell>
                    {columns.includes("hero_image") && (
                      <TableCell
                        style={{
                          border: "1px solid #D5D7DA",
                          backgroundColor: !restaurant?.imageUrl
                            ? "#FFF1F1"
                            : "transparent",
                        }}
                      >
                        <div className="flex item-center justify-center">
                          <img
                            src={restaurant?.imageUrl ?? ""}
                            className="h-[60px] w-[60px] rounded-[12px]"
                          />
                        </div>
                      </TableCell>
                    )}
                    {columns.includes("krunsh_score") && (
                      <TableCell
                        style={{
                          border: "1px solid #D5D7DA",
                          backgroundColor:
                            restaurant.krunshScore < 40
                              ? "#FFF1F1"
                              : restaurant.krunshScore < 80
                                ? "#FFF3EB"
                                : "#DEFBE6",
                        }}
                        className="text-center"
                      >
                        <Text fontSize={14} fontWeight={500}>
                          {restaurant.krunshScore}
                        </Text>
                      </TableCell>
                    )}
                    {columns.includes("rank") && (
                      <TableCell
                        style={{
                          border: "1px solid #D5D7DA",
                          backgroundColor:
                            restaurantsWith20PourcentBetterRank.includes(
                              restaurant.id,
                            )
                              ? "#DEFBE6"
                              : restaurantsWith20PourcentWorstRank.includes(
                                    restaurant.id,
                                  )
                                ? "#FFF1F1"
                                : "transparent",
                        }}
                        className="text-center"
                      >
                        <Text fontSize={14} fontWeight={500}>
                          {`${rank + 1} / ${nearbyRestaurants.length}`}
                        </Text>
                      </TableCell>
                    )}
                    {columns.includes("rating") && (
                      <TableCell
                        style={{
                          border: "1px solid #D5D7DA",
                          backgroundColor:
                            Number(restaurant.rating) < 3
                              ? "#FFF1F1"
                              : Number(restaurant.rating) < 4
                                ? "#FFF3EB"
                                : "#DEFBE6",
                        }}
                        className="text-center"
                      >
                        <Text fontSize={14} fontWeight={500}>
                          {restaurant.rating}/5
                        </Text>
                      </TableCell>
                    )}
                    {columns.includes("reviews_count") && (
                      <TableCell
                        style={{ border: "1px solid #D5D7DA" }}
                        className="text-center"
                      >
                        <Text fontSize={14} fontWeight={500}>
                          {restaurant.ratingCount}
                        </Text>
                      </TableCell>
                    )}
                    {columns.includes("delivery_time") && (
                      <TableCell
                        style={{
                          border: "1px solid #D5D7DA",
                          backgroundColor:
                            restaurant.deliveryTime > averageDeliveryTime
                              ? "#FFF1F1"
                              : "#DEFBE6",
                        }}
                        className="text-center"
                      >
                        <Text fontSize={14} fontWeight={500}>
                          {restaurant.deliveryTime} min
                        </Text>
                      </TableCell>
                    )}
                    {columns.includes("promotion_active") && (
                      <TableCell
                        style={{
                          border: "1px solid #D5D7DA",
                          backgroundColor:
                            restaurant.promotionCount > 0
                              ? "transparent"
                              : "#FFF1F1",
                        }}
                        className="text-center"
                      >
                        <Text fontSize={14} fontWeight={500}>
                          {restaurant.promotionCount}
                        </Text>
                      </TableCell>
                    )}
                    {columns.includes("promotion_type") && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <TableCell
                            onClick={() => {
                              if (promotionsText)
                                setOpenRestaurantPromotions(restaurant);
                            }}
                            style={{
                              cursor: promotionsText ? "pointer" : "default",
                              border: "1px solid #D5D7DA",
                              backgroundColor:
                                restaurant.promotionCount > 0
                                  ? "transparent"
                                  : "#FFF1F1",
                            }}
                            className="truncate max-w-[400px]"
                          >
                            <Text fontSize={14} fontWeight={500}>
                              {promotionsText ? (
                                <div className="flex items-center gap-[8px]">
                                  <Eye className="min-w-[20px] w-[20px]" />
                                  {promotionsText}
                                </div>
                              ) : (
                                t("no_promotion_detected")
                              )}
                            </Text>
                          </TableCell>
                        </TooltipTrigger>

                        <TooltipContent className="max-w-[600px]">
                          {promotionsText
                            ? promotionsText
                            : t("no_promotion_detected")}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {columns.includes("menu_items") && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <TableCell
                            onClick={() => {
                              setOpenRestaurantMenu(restaurant);
                            }}
                            style={{
                              border: "1px solid #D5D7DA",
                              cursor: "pointer",
                            }}
                            className="text-center"
                          >
                            <Text fontSize={14} fontWeight={500}>
                              <div className="flex items-center gap-[8px]">
                                <Eye className="min-w-[20px] w-[20px]" />
                                {restaurant.menuItems.length}
                              </div>
                            </Text>
                          </TableCell>
                        </TooltipTrigger>

                        <TooltipContent className="max-w-[600px]">
                          {itemsText}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <TableCell
                      style={{ border: "1px solid #D5D7DA" }}
                      className="text-center"
                    >
                      <Text fontSize={14} fontWeight={500}>
                        {
                          restaurant.menuItems.filter((item) => !item?.imageUrl)
                            .length
                        }
                      </Text>
                    </TableCell>
                    <TableCell
                      style={{ border: "1px solid #D5D7DA" }}
                      className="text-center"
                    >
                      <Text fontSize={14} fontWeight={500}>
                        {
                          restaurant.menuItems.filter(
                            (item) => !item?.description,
                          ).length
                        }
                      </Text>
                    </TableCell>
                    {columns.includes("shifts") && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <TableCell
                            style={{ border: "1px solid #D5D7DA" }}
                            className="truncate max-w-[400px]"
                          >
                            <Text fontSize={14} fontWeight={500}>
                              {statusShifts === "N/A"
                                ? "N.C"
                                : statusShifts === "closed"
                                  ? t("closed")
                                  : shiftsText}
                            </Text>
                          </TableCell>
                        </TooltipTrigger>

                        <TooltipContent className="max-w-[600px]">
                          {statusShifts === "N/A"
                            ? "N.C"
                            : orderDay.map((dayOfTheWeek) => (
                                <div>
                                  <Text color="white">{`${t(dayOfTheWeek)}:`}</Text>
                                  {shiftTooltip[dayOfTheWeek] ? (
                                    <ul className="mt-[4px] mb-[12px] pl-[4px]">
                                      {shiftTooltip[dayOfTheWeek].map(
                                        ({ startTime, endTime }) => (
                                          <li>{`${startTime} - ${endTime}`}</li>
                                        ),
                                      )}
                                    </ul>
                                  ) : (
                                    <Text color="white" className="ml-[4px]">
                                      {t("closed")}
                                    </Text>
                                  )}
                                </div>
                              ))}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {columns.includes("categories") && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <TableCell
                            style={{ border: "1px solid #D5D7DA" }}
                            className="truncate max-w-[400px]"
                          >
                            <Text fontSize={14} fontWeight={500}>
                              {categoriesText}
                            </Text>
                          </TableCell>
                        </TooltipTrigger>

                        <TooltipContent className="max-w-[600px]">
                          {categoriesText}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </div>
    </>
  );
};

export default Grid;
